import getSearchQueryDescription from '@dealpoint/lib/src/getSearchQueryDescription'
import { IContractSearchQuery, IKeyTerm, ITerm, ITermAnalyticsSearchResult } from '@dealpoint/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import omit from 'lodash/omit'

import { APIClient } from '../APIClient'
import { Utils } from '../Utils'
import { ITermAnalyticsSearch, PartialAtLeast, SearchId } from '../types'
import removeIndex from '../utils/array/removeIndex'
import searchDocuments from '../utils/document/pagedSearch'
import combineFilters from '../utils/searchQuery/combineFilters'
import createContractSearchQuery from '../utils/searchQuery/createContractSearchQuery'

// eslint-disable-next-line import/no-cycle
import { RootState } from './types'

// Helpers
const FIRST_SEARCH_ID = Utils.generateId()

const getDescription = (terms: ITerm[], searchQuery: IContractSearchQuery) => {
  const description = getSearchQueryDescription(terms, searchQuery)

  return !description || description.length === 0 ? 'All contracts' : description
}

interface CreateSearchProps {
  description: string
  id?: SearchId
  partial?: Partial<ITermAnalyticsSearch>
}

const _createSearch = ({
  description = 'Untitled',
  id,
  partial,
}: CreateSearchProps): ITermAnalyticsSearch => ({
  id: id ?? Utils.generateId(),
  description,
  searchQuery: createContractSearchQuery({ active: true, endTimeUnset: true, expired: true }),
  submittedSearchQuery: null,
  ...partial,
})

// Slice
interface ISearchesState {
  selectedId: SearchId
  searches: ITermAnalyticsSearch[]
}

interface ITermAnalyticsSearchesState extends Record<string, ISearchesState> {}

type PropsWithName<P> = P & { name: string }

export const initialState: ISearchesState = {
  selectedId: FIRST_SEARCH_ID,
  searches: [
    _createSearch({
      description: 'All contracts',
      id: FIRST_SEARCH_ID,
      partial: { loading: true },
    }),
  ],
}

const slice = createSlice({
  name: 'termAnalyticsSearches',
  initialState: {} as ITermAnalyticsSearchesState,
  reducers: {
    createSearch(state, action: PayloadAction<PropsWithName<{ description: string }>>) {
      const { description, name } = action.payload
      const newSearch = _createSearch({ description })
      state[name].searches.push(newSearch)
      state[name].selectedId = newSearch.id
    },
    deleteSearch(state, action: PayloadAction<PropsWithName<{ id: SearchId }>>) {
      const { name } = action.payload
      const { searches } = state[name]

      const index = searches.findIndex(({ id }) => id === action.payload.id)
      const newSearches = removeIndex(searches, index)
      const newIndex = newSearches.length > 1 ? Math.max(0, index - 1) : 0

      state[name] = { selectedId: newSearches[newIndex].id, searches: newSearches }
    },
    initializeSearches(state, action: PayloadAction<string>) {
      state[action.payload] = initialState
    },
    setSelectedSearch(state, action: PayloadAction<PropsWithName<{ id: SearchId }>>) {
      const { id, name } = action.payload
      state[name].selectedId = id
    },
    updateKeyTerm(
      state,
      action: PayloadAction<PropsWithName<{ id: SearchId; documentId: string; keyTerm: IKeyTerm }>>
    ) {
      const { documentId, keyTerm, id, name } = action.payload
      const index = state[name].searches.findIndex((s) => s.id === id)
      state[name].searches[index].results.records.map((r) =>
        r.id === documentId
          ? { ...r, keyTerms: r.keyTerms.map((k) => (k.id === keyTerm.id ? keyTerm : k)) }
          : r
      )
    },
    updateSearch(
      state,
      action: PayloadAction<PropsWithName<{ search: PartialAtLeast<ITermAnalyticsSearch, 'id'> }>>
    ) {
      const { name, search } = action.payload
      const index = state[name].searches.findIndex(({ id }) => id === search.id)
      const found = state[name].searches[index]
      state[name].searches[index] = {
        ...found,
        ...search,
        searchQuery: {
          ...found.searchQuery,
          ...search.searchQuery,
        },
      }
    },
  },
})

// Actions
interface GetSearchResultsProps {
  allTerms: ITerm[]
  id: SearchId
  name: string
  page?: number
  pageSize?: number
  url: string
}

export const getSearchResults = createAsyncThunk(
  'termAnalyticsSearches/getResults',
  async (
    { allTerms, id, name, page, pageSize, url }: GetSearchResultsProps,
    { dispatch, getState }
  ) => {
    const state = selectRoot(getState() as RootState)
    const search = state[name]?.searches.find((s) => s.id === id)
    if (!search) {
      return
    }

    dispatch(updateSearch({ name, search: { id, loading: true, error: false } }))
    const { searchQuery } = search
    try {
      const { results } = await searchDocuments<ITermAnalyticsSearchResult>({
        page,
        pageSize,
        searchQuery: {
          ...omit(searchQuery, ['contractInformationFilters', 'keyTermFilters']),
          filters: combineFilters(searchQuery),
        },
        url,
      })

      // Get the search description
      const description = getDescription(allTerms, searchQuery)

      dispatch(
        updateSearch({
          name,
          search: {
            id,
            description,
            loading: false,
            results,
            submittedSearchQuery: search.searchQuery,
          },
        })
      )
    } catch (error) {
      dispatch(updateSearch({ name, search: { id, error: true, loading: false } }))
    }
  }
)

interface InitializeSearchesProps extends Omit<GetSearchResultsProps, 'id'> {
  name: string
}

export const initializeSearches = createAsyncThunk(
  'termAnalyticsSearches/initializeSearches',
  ({ allTerms, name, pageSize, url }: InitializeSearchesProps, { dispatch, getState }) => {
    const state = selectRoot(getState() as RootState)
    const found = state[name]
    if (!found) {
      dispatch(slice.actions.initializeSearches(name))
      dispatch(getSearchResults({ allTerms, id: FIRST_SEARCH_ID, name, pageSize, url }))
    }
  }
)

interface GetKeyTermProps {
  documentId: string
  id: SearchId
  keyTermId: string
  name: string
}

export const getKeyTerm = createAsyncThunk(
  'termAnalyticsSearches/getKeyTerm',
  async ({ documentId, id, keyTermId, name }: GetKeyTermProps, { dispatch }) => {
    const keyTerm = await APIClient.makeAPICall('GET', `/docs/${documentId}/key-terms/${keyTermId}`)
    dispatch(updateKeyTerm({ documentId, keyTerm, id, name }))
  }
)

const { updateKeyTerm } = slice.actions

export const { createSearch, deleteSearch, setSelectedSearch, updateSearch } = slice.actions

// Selectors
const selectRoot = (state: RootState) => state.termAnalyticsSearches

export const makeSelectTermAnalyticsSearches = (name: string) =>
  createSelector(selectRoot, (state) => state[name]?.searches ?? [])

export const makeSelectTermAnalyticsSelectedSearch = (name: string) =>
  createSelector(selectRoot, (state) => {
    const { selectedId, searches } = state[name] ?? {}
    return searches?.find((s) => s.id === selectedId)
  })

// Reduce
export default slice.reducer
